import { useNavigate } from '@tanstack/react-router';

import { t } from '@web/i18n';
import { Button } from '@web/ui/core';

import { useAuthLogin } from '../hooks';
import { fireAndForget } from '@web/ui/core/utils';

/**
 * Authenticated but no org membership. Reached via redirect from `/`.
 * Renders an explanation plus a sign-out button — no automatic navigation,
 * so the previous redirect loop between `/` and `/login` is impossible.
 */
export function NoOrgPage(): React.JSX.Element {
  const { signOut } = useAuthLogin();
  const navigate = useNavigate();

  // signOut clears the token store but doesn't trigger navigation, so the
  // user would stay visually parked on /no-org. Route to /login explicitly
  // once sign-out resolves so route guards re-evaluate.
  const handleSignOut = async (): Promise<void> => {
    await signOut();
    await navigate({ to: '/login' });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="flex max-w-prose-sm flex-col items-center gap-4 text-center">
        <h1 className="text-display m-0 text-2xl md:text-display-lg font-medium">
          {t('auth.noOrg.title')}
        </h1>
        <p className="m-0 text-section-meta leading-[1.5] text-muted-foreground">
          {t('auth.noOrg.body')}
        </p>
        <Button
          variant="outline"
          onClick={() => {
            fireAndForget(handleSignOut());
          }}
        >
          {t('auth.noOrg.signOut')}
        </Button>
      </div>
    </main>
  );
}
